import { useState, useCallback } from 'react'
import { toast } from 'sonner'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip'

interface ClickToCopyProps {
  /** Texto completo a ser copiado */
  text: string
  /** Conteúdo visual exibido (pode ser JSX) */
  display: React.ReactNode
  /** Classes CSS adicionais */
  className?: string
  /** Texto do tooltip antes de copiar (default: mostra o texto completo) */
  tooltipLabel?: string
  /** Mensagem do toast ao copiar (default: "Copiado para a área de transferência") */
  toastMessage?: string
}

function fallbackCopy(text: string) {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

export function ClickToCopy({ text, display, className, tooltipLabel, toastMessage }: ClickToCopyProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text))
    setCopied(true)
    toast.success(toastMessage ?? 'Copiado para a área de transferência')
    setTimeout(() => setCopied(false), 1500)
  }, [text, toastMessage])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          onClick={handleCopy}
          onKeyDown={(e) => {
            // Como um botão: Enter e Espaço. A tecla não sobe — dentro de uma linha
            // clicável (a lista de peças) o Espaço abria a linha em vez de copiar.
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.preventDefault()
            handleCopy(e)
          }}
          className={`cursor-pointer transition-colors hover:bg-muted-foreground/20 ${className ?? ''}`}
        >
          {display}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        <span className="text-xs">{copied ? 'Copiado!' : (tooltipLabel ?? text)}</span>
      </TooltipContent>
    </Tooltip>
  )
}
